import React from "react";
import Select from "react-select";

const edsSelectStyles = {
  control: (base, state) => ({
    ...base,
    textAlign: "left",
    minHeight: "36px",
    borderRadius: "6px",
    borderColor: state.isFocused ? "#007372" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(0, 115, 114, 0.2)" : "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    backgroundColor: state.isDisabled ? "#f3f4f6" : "#ffffff",
    cursor: state.isDisabled ? "not-allowed" : "default",
    "&:hover": {
      borderColor: state.isDisabled ? "#d1d5db" : "#007372",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
  input: (base) => ({
    ...base,
    margin: "0",
    padding: "0",
    color: "#1f2937",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#6b7280",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#1f2937",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    padding: "4px 0",
    maxHeight: "200px",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#e5f5f5" : state.isFocused ? "#e5f5f5" : "transparent",
    color: state.isSelected ? "#007372" : "#1f2937",
    cursor: "pointer",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&:active": {
      backgroundColor: "#e5f5f5",
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#97989C",
    "&:hover": {
      color: "#6b7280",
      backgroundColor: "transparent",
    },
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: "#6b7280",
    padding: "4px 8px",
    cursor: "pointer",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.2s",
    "&:hover": {
      color: "#1f2937",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  groupHeading: (base) => ({
    ...base,
    fontSize: "12px",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    textAlign: "center",
    padding: "8px 12px 4px",
  }),
  group: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
};

const formatOptionLabel = ({ label, type }, { context }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
    {type && (
      <span style={{ 
        marginLeft: "8px", 
        fontSize: "12px", 
        color: "#6b7280", 
        textTransform: "capitalize",
        flexShrink: 0,
        textAlign: "right"
      }}>
        {context === "value" ? type : type}
      </span>
    )}
  </div>
);

export default function EdsSearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  isDisabled = false,
  isClearable = true,
  isSearchable = true,
  showTypeLabel = false,
  className = "eds-searchable-select",
  ...props
}) {
  const isGrouped = options.length > 0 && options[0]?.options !== undefined;

  const formattedOptions = isGrouped
    ? options.map((group) => ({
        label: group.label,
        options: group.options.map((opt) => ({
          value: opt.value || opt.id,
          label: opt.label,
          type: opt.type,
        })),
      }))
    : options.map((opt) => ({
        value: opt.value || opt.id,
        label: opt.label,
        type: opt.type,
      }));

  const flatOptions = isGrouped
    ? formattedOptions.flatMap((group) => group.options)
    : formattedOptions;

  const selectedOption = flatOptions.find((opt) => String(opt.value) === String(value)) || null;

  return (
    <Select
      options={formattedOptions}
      value={selectedOption}
      onChange={(selected) => onChange(selected ? selected.value : null)}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isSearchable={isSearchable}
      styles={edsSelectStyles}
      menuPlacement="bottom"
      menuPosition="fixed"
      menuPortalTarget={document.body}
      formatOptionLabel={showTypeLabel ? formatOptionLabel : undefined}
      className={className}
      classNamePrefix="eds-select"
      {...props}
    />
  );
}
